/**
 * VideoTextureManager
 * -------------------
 * Reference-counted registry of HTMLVideoElement instances keyed by source URL.
 * Two projectors pointing at the same clip share ONE decoder + frame buffer
 * instead of each running their own <video>. This is essential for multi-
 * projector blends where the same footage is fanned out to 4–8 surfaces: one
 * GPU decode vs eight.
 *
 * Side benefits:
 *  • "Global Sync" is automatic — every consumer of a URL sees the same frame
 *    because the frame buffer is literally shared.
 *  • Offscreen pause: when refcount drops to zero (no active projectors), we
 *    pause the video so the browser can release decoder resources.
 *  • Consumer limit: caps the number of simultaneously-active distinct videos
 *    to MAX_ACTIVE_VIDEOS to protect FPS on weaker GPUs.
 *
 * Intentionally tiny and framework-free — imported by ProjectionSystem.js and
 * any other surface (e.g. LED wall content, future in-scene video walls).
 */

const MAX_ACTIVE_VIDEOS = 5;

const registry = new Map(); // key: resolved URL → entry
// entry = { url, video: HTMLVideoElement, refs: Set<string>, ready: boolean }

function createVideoElement(url) {
  const video = document.createElement('video');
  // Properties required for browser autoplay + in-DOM-less playback:
  video.src = url;
  video.crossOrigin = 'anonymous';
  video.loop = true;
  video.muted = true;            // required for autoplay policies
  video.playsInline = true;
  video.preload = 'auto';
  // Keep video fully in memory decode path; never paints to the DOM.
  video.style.display = 'none';
  // Some browsers won't start decode until the element is in the DOM tree.
  document.body.appendChild(video);
  return video;
}

export function acquireVideo(url, consumerId) {
  if (!url || !consumerId) return null;
  let entry = registry.get(url);

  if (!entry) {
    // Cap active videos — if over the limit, refuse to create a new one
    // (caller should fall back to image/test-pattern for that projector).
    if (registry.size >= MAX_ACTIVE_VIDEOS) {
      // Find an entry with zero refs we can evict first
      const evictable = Array.from(registry.values()).find((e) => e.refs.size === 0);
      if (evictable) {
        releaseVideoEntry(evictable);
      } else {
        console.warn('[VideoTextureManager] max active videos reached; refusing new', url);
        return null;
      }
    }
    const video = createVideoElement(url);
    entry = { url, video, refs: new Set([consumerId]), ready: false };
    registry.set(url, entry);

    const onReady = () => { entry.ready = true; };
    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });

    // Attempt playback immediately; autoplay-blocked browsers will throw
    // but the next user gesture on the page will unlock it.
    const p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        console.warn('[VideoTextureManager] autoplay deferred:', err?.message || err);
      });
    }
  } else {
    entry.refs.add(consumerId);
    if (entry.video.paused) {
      entry.video.play().catch(() => {});
    }
  }
  return entry;
}

export function releaseVideo(url, consumerId) {
  const entry = registry.get(url);
  if (!entry) return;
  entry.refs.delete(consumerId);
  if (entry.refs.size === 0) {
    releaseVideoEntry(entry);
  }
}

function releaseVideoEntry(entry) {
  try { entry.video.pause(); } catch { /* ignore */ }
  try { entry.video.removeAttribute('src'); entry.video.load(); } catch { /* ignore */ }
  try { entry.video.remove(); } catch { /* ignore */ }
  registry.delete(entry.url);
}

/** Pause all active videos — used when editor goes offscreen / tab hidden. */
export function pauseAll() {
  registry.forEach((e) => { try { e.video.pause(); } catch { /* ignore */ } });
}

/** Resume all videos that still have active consumers. */
export function resumeAll() {
  registry.forEach((e) => {
    if (e.refs.size > 0) {
      try { e.video.play().catch(() => {}); } catch { /* ignore */ }
    }
  });
}

export function getVideoForProjector(projectorId, url) {
  const entry = registry.get(url);
  if (!entry || !entry.ready) return null;
  if (!entry.refs.has(projectorId)) return null;
  const v = entry.video;
  // readyState >= 2 (HAVE_CURRENT_DATA) means a frame is available to paint
  if (v.readyState < 2) return null;
  if (v.videoWidth === 0 || v.videoHeight === 0) return null;
  return v;
}

export function hasActiveVideos() {
  return registry.size > 0;
}

// Tab-visibility integration: pause all decoding when the tab is hidden so
// the optimization we just did isn't wasted by a hidden background tab.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pauseAll(); else resumeAll();
  });
}
